import React, { HTMLAttributes, forwardRef } from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'primary' | 'accent';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  dot?: boolean;
  pulse?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-brand-bg-warm text-brand-text-light border-brand-border',
  success: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-800',
  danger: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  info: 'bg-brand-primary-light text-brand-primary border-brand-primary/20',
  primary: 'bg-brand-primary-light text-brand-primary border-brand-primary/20',
  accent: 'bg-brand-accent-light text-brand-accent border-brand-accent/20',
};

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-brand-text-muted',
  success: 'bg-brand-success',
  warning: 'bg-yellow-500',
  danger: 'bg-red-500',
  info: 'bg-brand-primary',
  primary: 'bg-brand-primary',
  accent: 'bg-brand-accent',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', dot = false, pulse = false, className = '', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center gap-2 px-3 py-1.5 rounded-full
          text-xs font-bold uppercase tracking-wider border
          ${variantStyles[variant]}
          ${className}
        `}
        {...props}
      >
        {dot && (
          <span
            className={`w-2.5 h-2.5 rounded-full ${dotColors[variant]} ${pulse ? 'animate-pulse' : ''}`}
          />
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;
